import { useEffect, useRef, useState } from 'react'
import LegalResearch from '@services/legalResearch'
import { hasUsableResearch, pollResearchJob, researchContextKey, researchError, researchPending, researchFileDocument,
  acceptResearchJob, validResearchRetry, cancelAndRefreshResearchJob } from './legalResearchHelpers'
import { relevancePayload, reusableValidation, publicReusePayload, eligibilityAfterFeedback } from './researchRelevance'

export default function useLegalResearch(fields) {
  const contextKey = researchContextKey(fields)
  const currentKey = useRef(contextKey)
  currentKey.current = contextKey
  const operation = useRef(null)
  const [job, setJob] = useState(null)
  const [jobKey, setJobKey] = useState('')
  const [phase, setPhase] = useState('')
  const [error, setError] = useState('')
  const [eligibility, setEligibility] = useState({})
  const [pollRevision, setPollRevision] = useState(0)
  const [retryRevisionReady, setRetryRevisionReady] = useState(true)
  const currentJob = useRef(job)
  currentJob.current = job
  const researchId = job?.researchId
  const pending = researchPending(job)
  const stale = Boolean(job && jobKey !== contextKey)

  useEffect(() => () => operation.current?.abort(), [])
  useEffect(() => {
    operation.current?.abort()
    operation.current = null
    setPhase('')
  }, [contextKey])
  useEffect(() => {
    if (!researchId || !pending || stale || phase || error) return undefined
    const controller = new AbortController()
    const key = jobKey
    pollResearchJob({ researchId, get: LegalResearch.get, signal: controller.signal,
      isCurrent: () => currentKey.current === key && !operation.current && currentJob.current?.researchId === researchId,
      onJob: (data) => setJob((previous) => acceptResearchJob(previous, data)) })
      .catch((failure) => { if (!controller.signal.aborted) setError(failure.message) })
    return () => controller.abort()
  }, [researchId, pending, jobKey, stale, phase, error, pollRevision])

  const perform = async (label, action, apply) => {
    if (operation.current || currentKey.current !== contextKey || job &&
      (currentJob.current?.researchId !== job.researchId || currentJob.current?.revision !== job.revision)) return null
    const controller = new AbortController()
    const key = contextKey
    operation.current = controller
    setPhase(label)
    setError('')
    try {
      const response = await action(controller.signal)
      if (controller.signal.aborted || currentKey.current !== key) return null
      if (!response.success) { setError(researchError(response)); return null }
      return await apply(response.data, controller.signal, key)
    } catch (failure) {
      if (!controller.signal.aborted && currentKey.current === key)
        setError(failure.message || 'Não foi possível concluir a operação. Os dados foram mantidos.')
      return null
    } finally {
      if (operation.current === controller) { operation.current = null; setPhase('') }
    }
  }

  const refreshAfter = async (signal, key, minimumRevision = job.revision) => {
    const response = await LegalResearch.get(job.researchId, signal)
    if (signal.aborted || currentKey.current !== key) return null
    if (!response.success) throw new Error(researchError(response))
    if (response.data?.researchId !== job.researchId) throw new Error('A resposta não corresponde à pesquisa solicitada.')
    if (response.data.revision < minimumRevision) throw new Error('A revisão atual ainda não foi obtida. Atualize a pesquisa antes de repetir a operação.')
    setJob((previous) => acceptResearchJob(previous, response.data))
    return response.data
  }
  const start = () => {
    setEligibility({})
    return perform('Iniciando pesquisa…', (signal) => LegalResearch.start(fields, signal), (data, signal, key) => {
      if (!data?.researchId || !data.state) throw new Error('O servidor não retornou uma pesquisa válida.')
      if (signal.aborted || currentKey.current !== key) return null
      setJobKey(key)
      setJob(data)
      setRetryRevisionReady(true)
      return data
    })
  }
  const refresh = () => perform('Atualizando pesquisa…', (signal) => LegalResearch.get(job.researchId, signal), (data) => {
    if (data?.researchId !== job.researchId) throw new Error('A resposta não corresponde à pesquisa solicitada.')
    if (!retryRevisionReady && !(data.revision > job.revision))
      throw new Error('O cancelamento foi confirmado, mas a revisão atual ainda não foi obtida. Atualize a pesquisa antes de retomar.')
    setJob(data)
    setRetryRevisionReady(true)
    setPollRevision((value) => value + 1)
    return data
  })
  const cancel = () => perform('Cancelando pesquisa…', (signal) => cancelAndRefreshResearchJob({ job,
    cancel: LegalResearch.cancel, get: LegalResearch.get, signal, isCurrent: () => currentKey.current === contextKey,
    onConfirmed: (cancelled) => { setEligibility({}); setJob(cancelled); setRetryRevisionReady(false) } }), (data) => {
    setJob(data)
    setRetryRevisionReady(true)
    return data
  })
  const retry = () => {
    if (stale || !retryRevisionReady || !['Failed', 'Cancelled'].includes(job?.state)) return Promise.resolve(null)
    return perform('Retomando pesquisa…', (signal) => LegalResearch.retry(job.researchId, job.revision, signal), (data) => {
      if (!validResearchRetry(job, data))
        throw new Error('Não foi possível confirmar a retomada da pesquisa.')
      setEligibility({})
      setJob(data)
      return data
    })
  }
  const updateDocument = (action) => {
    if (stale || !['Completed', 'Partial'].includes(job?.state)) return Promise.resolve(null)
    return perform('Lendo e associando documento…', action, async (_, signal, key) => {
      setEligibility({})
      return refreshAfter(signal, key)
    })
  }
  const review = (validation, attestation) => {
    if (stale || !['Completed', 'Partial'].includes(job?.state)) return Promise.resolve(null)
    return perform('Registrando revisão…', (signal) => LegalResearch.review(job.researchId, job.revision,
      validation.validationId, attestation, signal), async (data, signal, key) => {
      const latest = await refreshAfter(signal, key)
      if (!latest || signal.aborted || currentKey.current !== key) return null
      if (data?.draftDecision?.eligibility === 'EligibleForReviewedDraft')
        setEligibility((previous) => ({ ...previous, [validation.validationId]: validation.validationHash }))
      else setError(data?.draftDecision?.eligibility === 'Disabled'
        ? 'O modo atual da pesquisa não autoriza referências na minuta. A revisão foi registrada para conferência.'
        : 'A revisão foi registrada, mas esta referência ainda não está autorizada para a minuta. Confira os alertas e os documentos.')
      return data
    })
  }
  const text = (versionId) => perform('Abrindo inteiro teor…', (signal) => LegalResearch.text(job.researchId, versionId, signal), (data) => data)
  const original = (versionId) => perform('Baixando documento…', (signal) => LegalResearch.original(job.researchId, versionId, signal), (data) => data)
  const feedback = (candidateId, issueId, decision, notes) => {
    if (stale || !['Completed', 'Partial'].includes(job?.state)) return Promise.resolve(null)
    const body = relevancePayload(job, candidateId, issueId, decision, notes)
    return perform('Registrando pertinência…', (signal) => LegalResearch.feedback(job.researchId, body, signal), async (data, signal, key) => {
      setEligibility((previous) => eligibilityAfterFeedback(previous, job.result, candidateId, issueId, decision))
      const latest = await refreshAfter(signal, key, data?.revision ?? job.revision)
      if (!latest || signal.aborted || currentKey.current !== key) return null
      return data
    })
  }
  const canReuse = (validation) => !phase && !stale && reusableValidation(job, contextKey, jobKey, eligibility, validation)
  const publicReuse = (validation, confirmed) => {
    if (!canReuse(validation)) return Promise.resolve(null)
    const body = publicReusePayload(job, validation.validationId, confirmed)
    return perform('Incluindo documento na base compartilhada…', (signal) => LegalResearch.publicReuse(job.researchId, body, signal), async (data, signal, key) => {
      const latest = await refreshAfter(signal, key)
      if (!latest || signal.aborted || currentKey.current !== key) return null
      return data
    })
  }

  return { job, stale, phase, error, pending, canRetry: !stale && retryRevisionReady && ['Failed', 'Cancelled'].includes(job?.state),
    canUse: !phase && hasUsableResearch(job, contextKey, jobKey, eligibility), canReuse,
    start, refresh, cancel, retry, review, feedback, publicReuse, text, original,
    attach: (candidateId, file) => updateDocument(async (signal) => LegalResearch.attach(job.researchId, job.revision, candidateId,
      await researchFileDocument(file, signal), signal)),
    importCandidate: (metadata, file, issueIds) => updateDocument(async (signal) => LegalResearch.importCandidate(job.researchId, job.revision,
      { ...metadata, ...await researchFileDocument(file, signal) }, issueIds, signal)),
    readSource: (url, issueIds) => updateDocument((signal) => LegalResearch.readSource(job.researchId, job.revision, url, issueIds, signal)) }
}
